// redux/slices/clientPhotosSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getServerUrl } from '../../Utils/serverSelector';
import { apiRequest } from '../../Utils/apiClient';
import { clearJwtToken } from '../../Utils/authStorage';

// 📌 Получение фото клиента (PRIVATE + JWT)
export const fetchClientPhoto = createAsyncThunk(
  'clientPhotos/fetchClientPhoto',
  async ({ id, uid, phone }, { rejectWithValue }) => {
    try {
      if (!uid || !phone) throw new Error('UID или phone не переданы');

      const response = await apiRequest(
        'GET',
        `/client-photos/${uid}/${phone}`
      );

      const filePath = response?.data?.filePath;
      const imageUrl = filePath
        ? `${getServerUrl()}${filePath}?timestamp=${Date.now()}`
        : null;

      return { id, imageUrl };
    } catch (error) {
      const status = error?.response?.status;

      // как было в старом коде: 404 и 403 считаем "фото нет"
      if ([404, 403].includes(status)) {
        return { id, imageUrl: null };
      }

      // 401 — токен битый, чистим
      if (status === 401) {
        try {
          await clearJwtToken();
        } catch { }
      }

      return rejectWithValue(error?.message || 'Failed to fetch client photo');
    }
  }
);

// 📌 Загрузка фото клиента (PRIVATE + JWT, multipart/form-data)
export const uploadClientPhoto = createAsyncThunk(
  'clientPhotos/uploadClientPhoto',
  async ({ id, uid, phone, imageFile }, { rejectWithValue }) => {
    try {
      if (!uid || !phone || !imageFile) {
        throw new Error('Не переданы uid / phone / imageFile');
      }

      const formData = new FormData();
      formData.append('uid', uid);
      formData.append('phone', phone);
      formData.append('file', imageFile);

      const response = await apiRequest(
        'POST',
        '/client-photos',
        formData,
        {
          // переопределяем дефолтный JSON на multipart
          'Content-Type': 'multipart/form-data',
        }
      );

      const filePath = response?.data?.filePath;
      const imageUrl = filePath
        ? `${getServerUrl()}${filePath}?timestamp=${Date.now()}`
        : null;

      return { id, imageUrl };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }
      return rejectWithValue(error?.message || 'Failed to upload client photo');
    }
  }
);

// 📌 Удаление фото клиента (PRIVATE + JWT)
export const deleteClientPhoto = createAsyncThunk(
  'clientPhotos/deleteClientPhoto',
  async ({ id, uid, phone }, { rejectWithValue }) => {
    try {
      if (!uid || !phone) throw new Error('UID или phone не переданы');

      await apiRequest(
        'DELETE',
        `/client-photos/${uid}/${phone}`
      );

      return id;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch { }
      }
      return rejectWithValue(error?.message || 'Failed to delete client photo');
    }
  }
);

// 📦 Slice
const clientPhotosSlice = createSlice({
  name: 'clientPhotos',
  initialState: {
    photos: {}, // { [id]: imageUrl }
    status: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchClientPhoto.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchClientPhoto.fulfilled, (state, action) => {
        const { id, imageUrl } = action.payload || {};

        state.status = "succeeded";

        if (id !== undefined && id !== null) {
          state.photos[id] = imageUrl || null;
        }
      })
      .addCase(fetchClientPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch client photo";
      })

      // UPLOAD / REPLACE
      .addCase(uploadClientPhoto.pending, (state) => {
        state.status = "uploading";
        state.error = null;
      })
      .addCase(uploadClientPhoto.fulfilled, (state, action) => {
        const { id, imageUrl } = action.payload || {};

        state.status = "succeeded";

        if (id !== undefined && id !== null) {
          state.photos[id] = imageUrl || null;
        }
      })
      .addCase(uploadClientPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to upload client photo";
      })

      // DELETE
      .addCase(deleteClientPhoto.pending, (state) => {
        state.status = "deleting";
        state.error = null;
      })
      .addCase(deleteClientPhoto.fulfilled, (state, action) => {
        const id = action.payload;

        state.status = "succeeded";

        if (id !== undefined && id !== null) {
          state.photos[id] = null;
        }
      })
      .addCase(deleteClientPhoto.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to delete client photo";
      });
  },
});

export default clientPhotosSlice.reducer;
